import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  BookOpenText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AiGuardNoticeList } from "@/components/ai/ai-guard-notice-list";
import {
  continuityIssueResolutionOrder,
  type AiAccessPromptView,
  type AiGuardNoticeView,
  type ContinuityIssueResolutionStatus,
  type ContinuityIssueView,
} from "@/features/ai";
import { useRuntimeCreditCost } from "@/features/pricing/useRuntimePricing";
import { cn } from "@/lib/utils";

type ContinuityFilter = "acknowledged" | "all" | "dismissed" | "open" | "resolved";

type ContinuityReviewDialogProps = {
  busy?: boolean;
  deleteBusyId?: string | null;
  errorMessage?: string | null;
  guardNotices?: AiGuardNoticeView[];
  issues?: ContinuityIssueView[];
  noCanonContext?: boolean;
  onDeleteIssue?: (issueId: string) => void;
  onOpenChange: (open: boolean) => void;
  onPromptAction?: () => void;
  onRunReview?: () => void;
  onSelectIssue?: (issueId: string) => void;
  onStatusChange?: (
    issueId: string,
    resolutionStatus: ContinuityIssueResolutionStatus,
  ) => void;
  open: boolean;
  prompt?: AiAccessPromptView | null;
  resolutionBusyId?: string | null;
  reviewSummary?: string | null;
  selectedIssueId?: string | null;
  statusLabel?: string | null;
};

const continuityFilters: ContinuityFilter[] = [
  "all",
  "open",
  "acknowledged",
  "resolved",
  "dismissed",
];

export function ContinuityReviewDialog({
  busy = false,
  deleteBusyId = null,
  errorMessage = null,
  guardNotices = [],
  issues = [],
  noCanonContext = false,
  onDeleteIssue,
  onOpenChange,
  onPromptAction,
  onRunReview,
  onSelectIssue,
  onStatusChange,
  open,
  prompt = null,
  resolutionBusyId = null,
  reviewSummary = null,
  selectedIssueId = null,
  statusLabel = null,
}: ContinuityReviewDialogProps) {
  const { t } = useTranslation();
  const continuityReviewCreditCost = useRuntimeCreditCost("continuity_review");
  const [activeFilter, setActiveFilter] = useState<ContinuityFilter>("all");

  const counts = useMemo(
    () => ({
      acknowledged: issues.filter((issue) => issue.resolutionStatus === "acknowledged").length,
      all: issues.length,
      dismissed: issues.filter((issue) => issue.resolutionStatus === "dismissed").length,
      open: issues.filter((issue) => issue.resolutionStatus === "open").length,
      resolved: issues.filter((issue) => issue.resolutionStatus === "resolved").length,
    }),
    [issues],
  );

  const filteredIssues = useMemo(() => {
    if (activeFilter === "all") {
      return issues;
    }

    return issues.filter((issue) => issue.resolutionStatus === activeFilter);
  }, [activeFilter, issues]);

  useEffect(() => {
    if (!open || filteredIssues.length === 0) {
      return;
    }

    const hasSelectedVisibleIssue = filteredIssues.some(
      (issue) => issue.id === selectedIssueId,
    );

    if (!hasSelectedVisibleIssue) {
      onSelectIssue?.(filteredIssues[0].id);
    }
  }, [filteredIssues, onSelectIssue, open, selectedIssueId]);

  function getSeverityIcon(issue: ContinuityIssueView) {
    switch (issue.severity) {
      case "critical":
        return <AlertCircle className="h-5 w-5 text-[#e64d43]" />;
      case "medium":
        return <AlertTriangle className="h-5 w-5 text-[#d8921b]" />;
      case "low":
      default:
        return <AlertCircle className="h-5 w-5 text-[#4971f5]" />;
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[90vh] w-[min(96vw,82rem)] gap-0 overflow-hidden border-[#eadcc7] bg-[#fffdf9] p-0 shadow-[0_28px_90px_-36px_rgba(56,40,22,0.28)] backdrop-blur-none">
        <DialogHeader className="border-b border-ink/10 px-8 pb-6 pt-8 pr-16">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-clay">
            {t("story.workspace.aiWorkspace", { defaultValue: "AI workspace" })}
          </p>
          <DialogTitle>{t("story.ai.continuity.title")}</DialogTitle>
          <DialogDescription className="max-w-4xl text-base leading-7 text-ink/70">
            {reviewSummary || t("story.ai.continuity.body")}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[calc(90vh-13rem)] overflow-y-auto">
          <div className="divide-y divide-[#ece2d6]">
            {errorMessage ? (
              <section className="px-8 py-6">
                <div
                  className="rounded-[1.2rem] border border-destructive/25 bg-destructive/5 px-4 py-4 text-sm text-destructive"
                  role="alert"
                >
                  {errorMessage}
                </div>
              </section>
            ) : null}

            {prompt ? (
              <section className="px-8 py-6">
                <div className="space-y-3 rounded-[1.2rem] border border-amber-300/40 bg-amber-50/90 px-4 py-4 text-sm text-amber-950">
                  <p>{t(prompt.descriptionKey, prompt.descriptionValues)}</p>
                  {prompt.ctaLabelKey && onPromptAction ? (
                    <Button onClick={onPromptAction} size="sm" type="button" variant="outline">
                      {t(prompt.ctaLabelKey)}
                    </Button>
                  ) : null}
                </div>
              </section>
            ) : null}

            {guardNotices.length > 0 ? (
              <section className="px-8 py-6">
                <AiGuardNoticeList notices={guardNotices} />
              </section>
            ) : null}

            <section className="px-8 py-6">
              <div className="flex flex-wrap gap-3">
                {continuityFilters.map((filter) => {
                  const active = filter === activeFilter;

                  return (
                    <button
                      className={cn(
                        "inline-flex min-w-[7rem] items-center justify-center rounded-[0.85rem] px-4 py-3 text-sm font-semibold transition-colors",
                        active
                          ? "bg-[#3b3129] text-[#fff9f0]"
                          : "bg-transparent text-ink/72 hover:bg-[#f5efe7]",
                      )}
                      key={filter}
                      onClick={() => setActiveFilter(filter)}
                      type="button"
                    >
                      {filter === "all"
                        ? t("story.ai.continuity.filters.all", { defaultValue: "All" })
                        : t(`story.ai.continuity.resolutions.${filter}`)}
                      <span className="ml-2 text-xs opacity-70">{counts[filter]}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="px-8 py-0">
              {noCanonContext ? (
                <div className="py-8 text-sm leading-7 text-muted-foreground">
                  {t("story.ai.continuity.noCanon")}
                </div>
              ) : filteredIssues.length === 0 ? (
                <div className="py-8 text-sm leading-7 text-muted-foreground">
                  {busy
                    ? t("story.ai.continuity.loading")
                    : t("story.ai.continuity.empty")}
                </div>
              ) : (
                <div className="divide-y divide-[#ece2d6]">
                  {filteredIssues.map((issue) => {
                    const expanded = issue.id === selectedIssueId;

                    return (
                      <article className="py-6" key={issue.id}>
                        <button
                          className="flex w-full items-start justify-between gap-4 text-left"
                          onClick={() => onSelectIssue?.(issue.id)}
                          type="button"
                        >
                          <div className="min-w-0 space-y-3">
                            <div className="flex flex-wrap items-center gap-3 text-sm font-semibold uppercase tracking-[0.16em]">
                              <span className="inline-flex items-center gap-2 text-foreground">
                                {getSeverityIcon(issue)}
                                {issue.severityLabel}
                              </span>
                              <Badge variant="outline">{issue.resolutionStatusLabel}</Badge>
                            </div>
                            <p className="max-w-4xl text-[1.05rem] font-semibold leading-8 text-foreground">
                              {issue.description}
                            </p>
                          </div>
                          {expanded ? (
                            <ChevronUp className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
                          )}
                        </button>

                        {expanded ? (
                          <div className="space-y-6 pt-6">
                            <div className="space-y-3">
                              <p className="text-sm uppercase tracking-[0.22em] text-[#a3896f]">
                                {t("story.ai.continuity.problemDetail", {
                                  defaultValue: "Problem detail",
                                })}
                              </p>
                              <p className="max-w-4xl text-base leading-8 text-ink/82">
                                {issue.description}
                              </p>
                            </div>

                            <div className="space-y-4 border-t border-[#ece2d6] pt-6">
                              <div className="flex items-center gap-3 text-sm uppercase tracking-[0.22em] text-[#a3896f]">
                                <BookOpenText className="h-4 w-4" />
                                <span>{t("story.ai.continuity.citations")}</span>
                              </div>

                              <div className="space-y-4">
                                {issue.citations.map((citation) => (
                                  <div className="space-y-2 border-l-2 border-[#e4d6c6] pl-4" key={citation.id}>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-base font-semibold text-foreground">
                                        {citation.sourceNameSnapshot}
                                      </span>
                                      {citation.canonStatusLabel ? (
                                        <Badge variant="outline">{citation.canonStatusLabel}</Badge>
                                      ) : null}
                                    </div>
                                    <p className="max-w-4xl text-sm leading-7 text-muted-foreground">
                                      {citation.evidenceSummary}
                                    </p>
                                    {citation.openHref ? (
                                      <a
                                        className="inline-flex text-sm font-semibold text-primary"
                                        href={citation.openHref}
                                      >
                                        {t("story.ai.continuity.openSource")}
                                      </a>
                                    ) : (
                                      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                        {t("story.ai.continuity.snapshotOnly")}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="space-y-4 border-t border-[#ece2d6] pt-6">
                              <p className="text-sm uppercase tracking-[0.22em] text-[#a3896f]">
                                {t("story.ai.continuity.actions", {
                                  defaultValue: "Actions",
                                })}
                              </p>
                              <div className="grid gap-3 sm:grid-cols-2 xl:max-w-3xl">
                                {continuityIssueResolutionOrder.map((status) => (
                                  <Button
                                    disabled={resolutionBusyId === issue.id}
                                    key={status}
                                    onClick={() => onStatusChange?.(issue.id, status)}
                                    type="button"
                                    variant={
                                      issue.resolutionStatus === status ? "default" : "outline"
                                    }
                                  >
                                    {t(`story.ai.continuity.resolutions.${status}`)}
                                  </Button>
                                ))}
                              </div>

                              {onDeleteIssue ? (
                                <Button
                                  disabled={deleteBusyId === issue.id}
                                  onClick={() => {
                                    if (!window.confirm(t("story.ai.continuity.deleteConfirm"))) {
                                      return;
                                    }

                                    onDeleteIssue(issue.id);
                                  }}
                                  type="button"
                                  variant="ghost"
                                >
                                  {deleteBusyId === issue.id
                                    ? t("story.ai.continuity.deleting")
                                    : t("story.ai.continuity.deleteAction")}
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>

        <DialogFooter className="border-t border-[#ece2d6] px-8 py-5 sm:justify-between">
          <p className="text-sm text-ink/60">
            {statusLabel ?? t("story.ai.continuity.creditStatus", {
              cost: continuityReviewCreditCost,
              remaining: 0,
            })}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
              {t("common.close")}
            </Button>
            <Button disabled={busy} onClick={onRunReview} type="button" variant="outline">
              {busy ? t("story.ai.continuity.running") : t("story.ai.continuity.runAction")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
